import React, { useState } from 'react';
import { Button, Input, Modal, Select, Table, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import SoftTag from '../components/SoftTag';
import { INQUIRY_STATUSES, getInquiryStatusTone } from '../constants/statuses';
import { formatDate } from '../utils/formatters';
import { useI18n } from '../i18n';

const { Option } = Select;
const { Title } = Typography;

export default function InquiriesPage({
  inquiries = [],
  leads = [],
  categories = [],
  openInquiryDetail,
  openLeadProfile,
  isSalesUser = false,
  onCreateInquiry,
  canDelete = true,
}) {
  const { t } = useI18n();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const query = searchText.trim().toLowerCase();
  const visibleInquiries = inquiries
    .filter((inquiry) => !inquiry.archived)
    .filter((inquiry) => statusFilter === 'all' || inquiry.status === statusFilter)
    .filter((inquiry) => categoryFilter === 'all' || inquiry.productCategoryName === categoryFilter)
    .filter((inquiry) => (
      !query
      || inquiry.id.toLowerCase().includes(query)
      || (inquiry.client || '').toLowerCase().includes(query)
    ));

  const columns = [
    {
      title: t('Inquiry ID'),
      dataIndex: 'id',
      key: 'id',
      render: (id, record) => (
        <Button type="link" onClick={() => openInquiryDetail(record, 'inquiries')} style={{ padding: 0, fontWeight: 600 }}>
          {id}
        </Button>
      ),
    },
    {
      title: t('Client Name'),
      dataIndex: 'client',
      key: 'client',
      render: (text, record) => {
        const lead = leads.find((item) => item.id === record.clientId);
        return (
          <Button type="link" onClick={() => { if (lead) openLeadProfile(lead, 'inquiries'); }} style={{ padding: 0 }}>
            {text}
          </Button>
        );
      },
    },
    {
      title: t('Created by'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 150,
    },
    {
      title: t('Created Date'),
      dataIndex: 'date',
      key: 'date',
      width: 130,
      render: (value) => formatDate(value),
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status) => <SoftTag tone={getInquiryStatusTone(status)}>{t(status)}</SoftTag>,
    },
    {
      title: t('Deadline'),
      dataIndex: 'neededBefore',
      key: 'neededBefore',
      width: 130,
      render: (value) => (value ? formatDate(value) : <span style={{ color: '#bfbfbf' }}>—</span>),
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => (
        canDelete && (
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => {
              Modal.confirm({
                title: t('Delete'),
                content: t('Are you sure you want to delete this entry?'),
                onOk() { message.success(t('Deleted')); },
              });
            }}
          >
            {t('Delete')}
          </Button>
        )
      ),
    },
  ];

  return (
    <div>
      <div className="page-toolbar" style={{ marginBottom: '16px' }}>
        <Title level={2} className="page-toolbar-title" style={{ margin: 0 }}>{t('Inquiries')}</Title>
        <div className="page-toolbar-controls">
          <Input
            placeholder={t('Search by inquiry ID, customer or product...')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300, height: 32, display: 'flex', alignItems: 'center' }}
            suffix={<SearchOutlined />}
          />
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 160 }}>
            <Option value="all">{t('All Statuses')}</Option>
            {INQUIRY_STATUSES.map((status) => <Option key={status} value={status}>{t(status)}</Option>)}
          </Select>
          <Select value={categoryFilter} onChange={setCategoryFilter} style={{ width: 180 }}>
            <Option value="all">{t('All Categories')}</Option>
            {categories.map((category) => (
              <Option key={category.id} value={category.name}>{category.name}</Option>
            ))}
          </Select>
          {isSalesUser && (
            <Button type="primary" icon={<PlusOutlined />} size="large" onClick={onCreateInquiry}>
              {t('New Inquiry')}
            </Button>
          )}
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={visibleInquiries}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 10, position: ['bottomCenter'] }}
        bordered
        size="middle"
        locale={{ emptyText: t('No inquiries match') }}
      />
    </div>
  );
}
